import axios from 'axios';
import { attach, getConfig } from 'retry-axios';
import { supabase } from './supabase';

const api = axios.create({
  timeout: 540000, // 9 minutes
  headers: {
    'Accept': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  }
});

// Configure retry-axios with more robust settings
attach();

api.defaults.raxConfig = {
  retry: 5, // Maximum number of retries
  retryDelay: (retryCount) => Math.min(Math.pow(2, retryCount) * 1000, 10000), // Exponential backoff with max delay of 10s
  statusCodesToRetry: [[408, 408], [500, 599]], // Include timeout status code
  httpMethodsToRetry: ['GET', 'HEAD', 'PUT', 'DELETE', 'OPTIONS', 'POST'],
  onRetryAttempt: (err) => {
    const cfg = getConfig(err);
    console.log(`Retry attempt #${cfg?.currentRetryAttempt}`);
  }
};

const uploadFile = async (file: File): Promise<string> => {
  try {
    // Check if file type is supported
    const supportedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!supportedTypes.includes(file.type)) {
      throw new Error(`Type de fichier non supporté: ${file.type}. Veuillez utiliser des images (JPG, PNG, GIF, ou WebP) ou des PDFs`);
    }

    // Check file size before upload
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error(`Le fichier ${file.name} est trop volumineux (${(file.size / 1024 / 1024).toFixed(2)}MB). La taille maximum est de 10MB`);
    }

    // Check if user is authenticated
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('Vous devez être connecté pour télécharger des fichiers');
    }

    // Generate a unique filename with original extension
    const fileExt = file.name.split('.').pop();
    const uniqueId = Math.random().toString(36).substring(2);
    const fileName = `${uniqueId}.${fileExt}`;
    const folderPath = `${user.id}/`;
    const filePath = `${folderPath}${fileName}`;

    // Upload the file with proper content type
    const { data, error: uploadError } = await supabase.storage
      .from('fiches')
      .upload(filePath, file, {
        contentType: file.type,
        upsert: false
      });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      if (uploadError.message.includes('duplicate')) {
        throw new Error(`Un fichier avec le même nom existe déjà: ${file.name}`);
      } else if (uploadError.message.includes('size')) {
        throw new Error(`Le fichier ${file.name} est trop volumineux. Maximum 10MB`);
      } else if (uploadError.message.includes('permission')) {
        throw new Error('Vous n\'avez pas les permissions nécessaires pour télécharger des fichiers');
      }
      throw new Error(`Erreur lors du téléchargement du fichier ${file.name}: ${uploadError.message}`);
    }

    // Get the public URL
    const { data: { publicUrl } } = supabase.storage
      .from('fiches')
      .getPublicUrl(filePath);

    return publicUrl;
  } catch (error) {
    console.error('Error in uploadFile:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Erreur lors du téléchargement du fichier');
  }
};

export const createFiche = async (params: URLSearchParams) => {
  try {
    // Production webhook URL
    const webhookUrl = 'https://n8n.srv757385.hstgr.cloud/webhook/255e532d-f7aa-4ca3-a5c5-2fc4a04022ea';

    // Verify webhook URL is valid before proceeding
    try {
      new URL(webhookUrl);
    } catch (e) {
      throw new Error('URL du webhook invalide. Veuillez contacter le support technique.');
    }

    // Get all files from the params if they exist
    const fileUrls: string[] = [];
    for (let i = 1; i <= 5; i++) {
      const fileParam = params.get(`file${i}`);
      if (fileParam) {
        try {
          // Convert base64 to File object
          const base64Data = fileParam.split(',')[1];
          const mimeType = fileParam.split(',')[0].split(':')[1].split(';')[0];
          const byteCharacters = atob(base64Data);
          const byteArrays = [];

          for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);
            
            for (let j = 0; j < slice.length; j++) {
              byteNumbers[j] = slice.charCodeAt(j);
            }
            
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
          }

          const blob = new Blob(byteArrays, { type: mimeType });
          const extension = mimeType.split('/')[1];
          const file = new File([blob], `document${i}.${extension}`, { type: mimeType });

          // Upload the file and get the URL
          const fileUrl = await uploadFile(file);
          fileUrls.push(fileUrl);
        } catch (err) {
          console.error(`Error processing file ${i}:`, err);
          throw err;
        }
      }
    }

    // Remove the original file parameters
    for (let i = 1; i <= 5; i++) {
      params.delete(`file${i}`);
    }

    // Always add document_url_1 to document_url_5 parameters
    for (let i = 1; i <= 5; i++) {
      params.set(`document_url_${i}`, fileUrls[i - 1] || '');
    }

    // Make the request with proper error handling and increased timeout
    const response = await api.get(`${webhookUrl}?${params.toString()}`, {
      timeout: 540000, // 9 minutes
      validateStatus: (status) => status >= 200 && status < 600, // Handle all responses
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });

    // Handle error responses
    if (response.status >= 400) {
      throw new Error(
        response.data?.message || 
        `Le serveur a retourné une erreur (${response.status}). Veuillez réessayer dans quelques instants.`
      );
    }

    return response.data;
  } catch (error) {
    console.error('Error in createFiche:', error);

    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        throw new Error(
          'Le serveur met trop de temps à répondre. ' +
          'Veuillez patienter quelques minutes puis réessayer. ' +
          'Notre système est en train de générer votre fiche avec soin.'
        );
      }
      if (error.message.includes('Network Error')) {
        throw new Error(
          'Le serveur n8n est temporairement indisponible. ' +
          'Veuillez réessayer dans quelques minutes. ' +
          'Si le problème persiste, veuillez contacter le support technique.'
        );
      }
      if (error.response?.status === 500) {
        throw new Error(
          'Le serveur a rencontré une erreur lors du traitement de votre demande. ' +
          'Veuillez réessayer dans quelques minutes.'
        );
      }
      throw new Error(
        error.response?.data?.message || 
        'Une erreur est survenue lors de la création de la fiche. Veuillez réessayer.'
      );
    }
    
    if (error instanceof Error) {
      throw error;
    }
    
    throw new Error('Une erreur inattendue est survenue. Veuillez réessayer dans quelques minutes.');
  }
};